/**
 * Pobranie tokenu captchy przed wysyłką formularza.
 *
 * KLUCZOWE: ta funkcja NIE MOŻE rzucić ani wisieć w nieskończoność. Wywołanie siedzi
 * na ścieżce klika „Przejdź do weryfikacji" (assets/js/form/ajax-form.js) i każde
 * niedomknięte tu żądanie zostawiało użytkownika z trwale zablokowanym przyciskiem —
 * bez requestu, bez komunikatu, bez zdarzenia `submit-success` (czyli i bez trackingu
 * Adobe). Zgłoszenie od Digital Intel z 19-08-2026 („Go to verification button does not
 * redirect, gets stuck loading") to dokładnie ten przypadek.
 *
 * Realne przyczyny awarii dostawcy captchy: skrypt zablokowany przez adblocka / firmowe
 * proxy / narzędzie zgód (wtedy `querySelector` zwraca null), albo kliknięcie zanim
 * `grecaptcha` zdąży się zainicjalizować — skrypt Google wisi na końcu <body> bez `defer`.
 *
 * Przy niepowodzeniu pole tokenu zostaje puste i wysyłka idzie dalej. To świadomie
 * fail-visible, nie fail-open: serwer odrzuci puste pole błędem walidacji, który
 * użytkownik zobaczy i będzie mógł ponowić. Ominięcie captchy byłoby dziurą.
 */

// Ile czekamy na token, zanim uznamy dostawcę captchy za niedostępny.
const TOKEN_TIMEOUT_MS = 5000;

// Ile czekamy na samą inicjalizację `grecaptcha` (skrypt może się jeszcze ładować).
const READY_TIMEOUT_MS = 3000;

function timeout(ms, label) {
    return new Promise((resolve, reject) => {
        setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    });
}

function scriptParam(srcPrefix, param) {
    const script = document.querySelector(`script[src^="${srcPrefix}"]`);

    if (!script) {
        throw new Error(`Captcha script not found: ${srcPrefix}`);
    }

    return new URLSearchParams(new URL(script.src).search).get(param);
}

/**
 * Google wymaga dla v3 odpalenia `execute()` dopiero po `grecaptcha.ready()`. Wcześniejsze
 * wywołanie rzuca („reCAPTCHA has not been loaded"), a przy wolnym łączu użytkownik zdąży
 * kliknąć przed inicjalizacją. `ready()` samo w sobie nie ma limitu czasu, więc dokładamy swój.
 */
function whenReady(api) {
    if (!api || typeof api.ready !== 'function') {
        return Promise.reject(new Error('grecaptcha is not available'));
    }

    return Promise.race([
        new Promise((resolve) => api.ready(resolve)),
        timeout(READY_TIMEOUT_MS, 'grecaptcha.ready'),
    ]);
}

export function getRecaptcha(type) {
    if (type === 'v3') {
        const key = scriptParam('https://www.google.com/recaptcha/api.js', 'render');

        return whenReady(window.grecaptcha).then(() => window.grecaptcha.execute(key));
    }
    if (type === 'enterprise') {
        const key = scriptParam('https://www.google.com/recaptcha/enterprise.js', 'render');

        return whenReady(window.grecaptcha?.enterprise).then(() => window.grecaptcha.enterprise.execute(key));
    }
    if (type === 'captcha4oc') {
        return window.Captcha4oc.getToken('submit');
    }

    return Promise.reject(new Error(`Unknown captcha type: ${type}`));
}

export async function setRecaptchaResponse(form) {
    if ($(form).data('recaptcha') && !$(form.elements).filter('.g-recaptcha-response').length) {
        const fieldName = typeof $(form).data('recaptcha') == 'string' ? $(form).data('recaptcha') : 'g-recaptcha-response';
        $(form).append(`<input type="hidden" name="${fieldName}" class="g-recaptcha-response">`);
    }

    const $recaptchaField = $(form.elements).filter('.g-recaptcha-response');

    if (!$recaptchaField.length) {
        return;
    }

    const type = $recaptchaField.data('recaptcha-type') || 'v3'; // domyślnie v3

    try {
        const token = await Promise.race([
            Promise.resolve().then(() => getRecaptcha(type)),
            timeout(TOKEN_TIMEOUT_MS, 'captcha token'),
        ]);

        $recaptchaField.val(token);
    } catch (error) {
        // Pusty token = błąd walidacji z serwera, który użytkownik zobaczy. Lepsze niż martwy przycisk.
        $recaptchaField.val('');
        console.warn('Captcha unavailable, submitting without token:', error);
    }
}
